/**
 * Organization access grants on a user (users.orgAccess). The server owns the
 * field: changes go through the setUserOrgAccess callable, previewed with a dry
 * run that resolves the companies first. 'view' reads every company of the
 * organization and the ones below it; 'member' has full company rights there.
 */
import React, { useMemo, useState } from 'react';
import {
    Box, Typography, Alert, Button, Chip, Stack, Autocomplete, TextField, Select, MenuItem,
    FormControl, IconButton, CircularProgress, Tooltip
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon, Add as AddIcon } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import { orgAccessGrants, orgAccessCompanyIds } from '../../../utils/orgAccess';

const SMALL = { fontSize: '12px' };
const sameGrants = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const levelChip = (level) => (
    <Chip
        size="small"
        label={level === 'member' ? 'Member' : 'View only'}
        sx={{ height: 20, fontSize: '10px', bgcolor: level === 'member' ? '#dcfce7' : '#eff6ff', color: level === 'member' ? '#0a875a' : '#1d4ed8' }}
    />
);

const CompanyChips = ({ ids, companyMap, empty }) => (
    ids.length === 0 ? (
        <Typography sx={{ ...SMALL, color: '#9ca3af' }}>{empty}</Typography>
    ) : (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {ids.map(id => (
                <Chip key={id} size="small" label={companyMap[id] ? `${companyMap[id]} (${id})` : id} sx={{ height: 20, fontSize: '10px' }} />
            ))}
        </Stack>
    )
);

const OrganizationAccessSection = ({ uid, user, organizations, organizationMap, companyMap, canEdit, onSaved }) => {
    const savedGrants = useMemo(() => orgAccessGrants(user), [user]);
    const resolved = useMemo(() => orgAccessCompanyIds(user), [user]);
    const [draft, setDraft] = useState(null); // null = not editing
    const [addOrg, setAddOrg] = useState(null);
    const [addLevel, setAddLevel] = useState('view');
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const grants = draft || savedGrants;
    const changed = draft !== null && !sameGrants(draft, savedGrants);
    const nameOf = (orgID) => organizationMap[orgID] || orgID;

    const startEdit = () => { setDraft(savedGrants); setPreview(null); setError(null); };
    const cancelEdit = () => { setDraft(null); setPreview(null); setError(null); setAddOrg(null); };
    const updateDraft = (next) => { setDraft(next); setPreview(null); };

    const addGrant = () => {
        if (!addOrg) return;
        updateDraft([...grants.filter(g => g.orgID !== addOrg.id), { orgID: addOrg.id, level: addLevel }]);
        setAddOrg(null);
    };

    const run = async (dryRun) => {
        setBusy(true);
        setError(null);
        try {
            const call = httpsCallable(functions, 'setUserOrgAccess');
            const { data } = await call({ uid, grants: draft, dryRun });
            if (dryRun) {
                setPreview(data);
            } else {
                onSaved?.({
                    grants: Array.isArray(data?.grants) ? data.grants : draft,
                    grantOrgIDs: (Array.isArray(data?.grants) ? data.grants : draft).map(g => g.orgID),
                    memberCompanyIDs: Array.isArray(data?.memberCompanyIDs) ? data.memberCompanyIDs : [],
                    viewCompanyIDs: Array.isArray(data?.viewCompanyIDs) ? data.viewCompanyIDs : [],
                });
                cancelEdit();
            }
        } catch (err) {
            setError(err?.message || 'Request failed');
        } finally {
            setBusy(false);
        }
    };

    const orgOptions = (organizations || []).filter(o => !grants.some(g => g.orgID === o.id));

    return (
        <Box sx={{ mt: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>Organization access</Typography>
                {canEdit && draft === null && (
                    <Button size="small" onClick={startEdit} sx={SMALL}>Edit</Button>
                )}
            </Stack>
            <Alert severity="info" sx={{ mb: 2, ...SMALL }}>
                A grant gives this user data access to every company in the organization and in the organizations
                below it. View only: read shipments, customers, addresses and invoices. Member: full company rights,
                like a connected company. Connected organizations above do not grant data access.
            </Alert>

            {grants.length === 0 ? (
                <Typography sx={{ ...SMALL, color: '#9ca3af', mb: 2 }}>No organization access grants.</Typography>
            ) : (
                <Stack spacing={1} sx={{ mb: 2 }}>
                    {grants.map(g => (
                        <Box key={g.orgID} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e5e7eb', borderRadius: 1 }}>
                            <Typography sx={{ ...SMALL, fontWeight: 500, color: '#111827' }}>{nameOf(g.orgID)}</Typography>
                            <Typography sx={{ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }}>{g.orgID}</Typography>
                            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                                {draft !== null ? (
                                    <FormControl size="small">
                                        <Select
                                            value={g.level}
                                            onChange={(e) => updateDraft(grants.map(x => (x.orgID === g.orgID ? { ...x, level: e.target.value } : x)))}
                                            sx={{ fontSize: '12px', '& .MuiSelect-select': { py: '4px' } }}
                                            disabled={busy}
                                        >
                                            <MenuItem value="view" sx={SMALL}>View only</MenuItem>
                                            <MenuItem value="member" sx={SMALL}>Member</MenuItem>
                                        </Select>
                                    </FormControl>
                                ) : levelChip(g.level)}
                                {draft !== null && (
                                    <Tooltip title="Remove grant">
                                        <IconButton size="small" onClick={() => updateDraft(grants.filter(x => x.orgID !== g.orgID))} disabled={busy}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Stack>
            )}

            {draft !== null && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <Autocomplete
                        size="small"
                        sx={{ minWidth: 260, flex: 1 }}
                        options={orgOptions}
                        value={addOrg}
                        onChange={(e, v) => setAddOrg(v)}
                        getOptionLabel={(o) => `${o.name} (${o.id})`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => <TextField {...params} label="Organization" />}
                        disabled={busy}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select value={addLevel} onChange={(e) => setAddLevel(e.target.value)} sx={SMALL} disabled={busy}>
                            <MenuItem value="view" sx={SMALL}>View only</MenuItem>
                            <MenuItem value="member" sx={SMALL}>Member</MenuItem>
                        </Select>
                    </FormControl>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addGrant} disabled={!addOrg || busy} sx={SMALL}>
                        Add
                    </Button>
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2, ...SMALL }}>{error}</Alert>}

            {preview ? (
                <Box sx={{ p: 1.5, mb: 2, border: '1px solid #bfdbfe', borderRadius: 1, bgcolor: '#f8fbff' }}>
                    <Typography sx={{ ...SMALL, fontWeight: 600, mb: 1 }}>Preview — not saved yet</Typography>
                    <Typography sx={{ ...SMALL, color: '#374151', mb: 0.5 }}>
                        Member companies ({(preview.memberCompanyIDs || []).length})
                    </Typography>
                    <CompanyChips ids={preview.memberCompanyIDs || []} companyMap={companyMap} empty="None" />
                    <Typography sx={{ ...SMALL, color: '#374151', mt: 1, mb: 0.5 }}>
                        View-only companies ({(preview.viewCompanyIDs || []).length})
                    </Typography>
                    <CompanyChips ids={preview.viewCompanyIDs || []} companyMap={companyMap} empty="None" />
                </Box>
            ) : draft === null && (
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ ...SMALL, color: '#374151', mb: 0.5 }}>Member companies ({resolved.member.length})</Typography>
                    <CompanyChips ids={resolved.member} companyMap={companyMap} empty="None" />
                    <Typography sx={{ ...SMALL, color: '#374151', mt: 1, mb: 0.5 }}>View-only companies ({resolved.view.length})</Typography>
                    <CompanyChips ids={resolved.view} companyMap={companyMap} empty="None" />
                </Box>
            )}

            {draft !== null && (
                <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={cancelEdit} disabled={busy} sx={SMALL}>Cancel</Button>
                    {!preview ? (
                        <Button size="small" variant="contained" onClick={() => run(true)} disabled={!changed || busy} sx={SMALL}
                            startIcon={busy ? <CircularProgress size={12} color="inherit" /> : null}>
                            Preview
                        </Button>
                    ) : (
                        <Button size="small" variant="contained" onClick={() => run(false)} disabled={busy} sx={SMALL}
                            startIcon={busy ? <CircularProgress size={12} color="inherit" /> : null}>
                            Save
                        </Button>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default OrganizationAccessSection;
